import {
  Component,
  ChangeDetectionStrategy,
  Inject,
  ViewChild,
} from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { filter, Observable, of, tap } from 'rxjs';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import cloneDeep from 'lodash.clonedeep';

import { StaticMessage } from '@shared/enums';
import {
  DprActivityAttachmentsGridData,
  DprActivityCustomDataGridData,
  DprActivityGridData,
} from '@dpr-details/models/grid';
import { DprActivityData } from '@dpr-details/enums';
import { BaseDialogComponent } from '@shared/models/component';
import { AppFacade, UserDetailsFacade } from '@shared/state/facades';
import {
  DprAttachmentsComponent,
  DprAttachmentsGridComponent,
  DprEditActivityCustomDataGridComponent,
} from '..';
import {
  DialogComponent,
  DialogContext,
  DialogData,
  GridSync,
  GridApi,
} from '@grid/models';
import { DprSubActivityGridData } from '@dpr-details/models/grid';
import { DprEditActivitySubActivitiesGridComponent } from '../dpr-edit-activity-subactivities-grid/dpr-edit-activity-subactivities-grid.component';
import { SelectedSubproject } from '@shared/models';
import {
  ActivityMobilisation,
  CustomData,
  CustomDataValue,
} from '@shared/models/project-setup';
import { DprActivityCustomDataService } from '@dpr-details/services';
import { DprAttachmentsGridData } from '@shared/models/grid';
import { DprAttachmentService } from '@shared/services/dpr';
import { canDeactivateUnsavedChanges } from '@shared/guards';

@UntilDestroy()
@Component({
  selector: 'app-dpr-edit-activity-dialog-edit',
  templateUrl: './dpr-edit-activity-dialog-edit.component.html',
  styleUrls: ['./dpr-edit-activity-dialog-edit.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DprEditActivityDialogEditComponent
  extends BaseDialogComponent
  implements DialogComponent<DprActivityGridData>
{
  //#region Templates

  @ViewChild('subActivitiesGrid', { static: true })
  subActivitiesGrid: DprEditActivitySubActivitiesGridComponent;

  @ViewChild('customDataGrid', { static: true })
  customDataGrid: DprEditActivityCustomDataGridComponent;

  @ViewChild('attachmentsComponent', { static: true })
  attachmentsComponent: DprAttachmentsComponent;

  //#endregion

  title: string;
  context: DialogContext<DprActivityGridData>;
  selectedSubproject: SelectedSubproject;

  activityMobilisation: ActivityMobilisation[];

  activeLink: DprActivityData;
  DprActivityData = DprActivityData;
  userDetailsQuery = this.userDetailsFacade.query;

  isAppOnline$ = this.appFacade.query.isAppOnline$;
  subActivitiesGridApi: GridApi<DprSubActivityGridData>;
  customDataGridApi: GridApi<DprActivityCustomDataGridData>;
  attachmentsGridApi: GridApi<DprAttachmentsGridData>;

  dprActivityCustomDataGridData: DprActivityCustomDataGridData[] = [];
  dprActivityAttachmentsGridData: DprAttachmentsGridData[] = [];
  customDataList: CustomData[] = [];
  customDataListValues: CustomDataValue[] = [];
  initialCustomData: DprActivityCustomDataGridData[];

  StaticMessage = StaticMessage;

  isMilestoneActivity: boolean;

  constructor(
    protected dialogRef: MatDialogRef<DprEditActivityDialogEditComponent>,
    private appFacade: AppFacade,
    @Inject(MAT_DIALOG_DATA)
    public data: DialogData<DprActivityGridData>,
    private dprActivityCustomDataService: DprActivityCustomDataService,
    private dprAttachmentService: DprAttachmentService,
    private userDetailsFacade: UserDetailsFacade
  ) {
    super(dialogRef);

    this.context = this.data.context;

    this.initData();
  }

  private get syncDprSubActivities(): GridSync<DprSubActivityGridData> {
    const changes = this.subActivitiesGridApi.getChanges();

    return changes;
  }

  private get syncDprActivityCustomDataList(): GridSync<DprActivityCustomDataGridData> {
    const changes = this.customDataGridApi.getChanges();

    changes.addedList = changes.updatedList.filter((x) => isNaN(+x.id));
    changes.updatedList = changes.updatedList.filter((x) => !isNaN(+x.id));

    return changes;
  }

  private get syncDprActivityAttachments(): GridSync<DprActivityAttachmentsGridData> {
    const changes =
      this.attachmentsGridApi.getChanges() as GridSync<DprActivityAttachmentsGridData>;

    return changes;
  }

  private get isDirty(): boolean {
    return (
      this.subActivitiesGrid?.api.isDirty() ||
      this.customDataGrid?.api.isDirty() ||
      this.attachmentsGrid?.api.isDirty()
    );
  }

  private get attachmentsGrid(): DprAttachmentsGridComponent {
    return this.attachmentsComponent.dprAttachmentsGrid;
  }

  onDone(): void {
    if (!this.isDirty) {
      this.onCancel();
      return;
    }

    const syncDprSubActivities = this.syncDprSubActivities;
    const syncDprActivityCustomDataList = this.syncDprActivityCustomDataList;
    const syncDprActivityAttachments = this.syncDprActivityAttachments;

    if (
      !this.subActivitiesGrid.validateChanges() ||
      !this.dprAttachmentService.validateChanges(syncDprActivityAttachments)
    ) {
      return;
    }

    this.dialogRef.close({
      syncDprSubActivities,
      syncDprActivityCustomDataList,
      syncDprActivityAttachments,
      dprSubActivities: this.context.data.dprSubActivities.filter(
        (x) => !this.subActivitiesGrid.isRowEmpty(x)
      ),
      dprActivityCustomDataList: this.dprActivityCustomDataGridData,
      dprActivityAttachments: this.dprActivityAttachmentsGridData.filter(
        (x) => !this.attachmentsGrid.isRowEmpty(x)
      ),
    });
  }

  onCancel(): void {
    if (!this.isDirty) {
      this.dialogRef.close();
      return;
    }

    canDeactivateUnsavedChanges(this, this._dialog)
      .pipe(
        untilDestroyed(this),
        filter((val) => val),
        tap(() => {
          this.dialogRef.close();
        })
      )
      .subscribe();
  }

  canDeactivate(): boolean | Observable<boolean> {
    return !this.isDirty;
  }

  save(): Observable<boolean> {
    this.onDone();
    return of(false);
  }

  onSubActivitiesGridRegistered(
    gridApi: GridApi<DprSubActivityGridData>
  ): void {
    this.subActivitiesGridApi = gridApi;

    const data = this.context.data.syncDprSubActivities;

    if (data) {
      this.subActivitiesGridApi.setPredefinedChanges({
        addedList: data.addedList,
        updatedList: data.updatedList,
        deletedIds: data.deletedIds,
        changedProperties: data.changedProperties,
      });
    }
  }

  onCustomDataGridRegistered(
    gridApi: GridApi<DprActivityCustomDataGridData>
  ): void {
    this.customDataGridApi = gridApi;

    const data = this.context.data.syncDprActivityCustomDataList;
    if (data) {
      this.customDataGridApi.setPredefinedChanges({
        addedList: [],
        updatedList: [...data.addedList, ...data.updatedList],
        deletedIds: data.deletedIds,
        changedProperties: data.changedProperties,
      });
    }
  }

  onAttachmentsGridRegistered(gridApi: GridApi<DprAttachmentsGridData>): void {
    this.attachmentsGridApi = gridApi;

    const data = this.context.data.syncDprActivityAttachments;
    if (data) {
      this.attachmentsGridApi.setPredefinedChanges({
        addedList: data.addedList,
        updatedList: data.updatedList,
        deletedIds: data.deletedIds,
        changedProperties: data.changedProperties,
      });
    }
  }

  //#region Private methods

  private initData(): void {
    this.context.data = cloneDeep(this.context.data);
    this.activityMobilisation =
      this.context.additionalData.activityMobilisation;

    this.isMilestoneActivity = this.context.additionalData?.isMilestoneActivity;
    this.selectedSubproject = this.context.additionalData.selectedSubproject;

    this.initialCustomData = this.context.data.dprActivityCustomDataList;
    this.dprActivityAttachmentsGridData =
      this.context.data.dprActivityAttachments;

    this.initSubprojectRelatedData();

    this.activeLink =
      this.context.additionalData?.selectedTab ?? DprActivityData.CustomData;
  }

  private initSubprojectRelatedData(): void {
    this.title = $localize`:@@dprDetails.dprActivitiesGrid.titleEditDetails:${this.context.additionalData.title}:activityIdentifier:: Edit details`;

    this.customDataList = this.selectedSubproject.data.customDataList;

    this.customDataListValues =
      this.selectedSubproject.data.customDataValueList;

    const customDataGridData =
      this.dprActivityCustomDataService.buildCustomDataGridData(
        this.selectedSubproject,
        this.context.data
      );

    this.dprActivityCustomDataGridData = customDataGridData
      .map((entry) => ({
        ...entry,
        booleanTerminologyId: entry.booleanTerminologyId,
        dataTypeId: this.customDataList.find(
          (cd) => cd.id === entry.customDataId
        ).dataTypeId,
      }))
      .sort((x, y) => x.priority - y.priority);
  }

  //#endregion
}
